import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, FileText, Hammer, Calendar, UsersRound,
  Wallet, BarChart3, MessageCircle, Settings, LogOut, Menu, X, ShieldCheck, ListChecks, Truck, Clock, Shuffle,
} from "lucide-react";
import logoFull from "../assets/tenditaly-logo-full.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/attivita", label: "Attività", icon: ListChecks, office: true },
  { to: "/clienti", label: "Clienti", icon: Users, perm: "view_clients" },
  { to: "/preventivi", label: "Preventivi", icon: FileText, perm: "view_quotes" },
  { to: "/lavori", label: "Lavori", icon: Hammer },
  { to: "/calendario", label: "Calendario", icon: Calendar },
  { to: "/squadre", label: "Squadre", icon: UsersRound, perm: "manage_teams" },
  { to: "/incassi", label: "Incassi", icon: Wallet, perm: "view_payments" },
  { to: "/incassi-diversi", label: "Incassi diversi", icon: Shuffle, perm: "view_payments" },
  { to: "/fornitori", label: "Fornitori", icon: Truck, office: true },
  { to: "/ore", label: "Orario personale", icon: Clock, office: true },
  { to: "/statistiche", label: "Statistiche", icon: BarChart3, perm: "view_stats" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, perm: "manage_whatsapp" },
  { to: "/accessi", label: "Accessi", icon: ShieldCheck, perm: "manage_users" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, perm: "manage_settings" },
];

export default function Layout({ children }) {
  const { user, can, logout, isOffice } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((it) => (!it.perm || can(it.perm)) && (!it.office || isOffice));

  const doLogout = async () => { await logout(); nav("/login"); };

  const SideContent = () => (
    <>
      <div className="px-5 py-5 border-b border-border flex items-center">
        <img src={logoFull} alt="TENDItaly" className="h-10 object-contain" />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} data-testid={`nav-${it.label.toLowerCase().replace(/\s/g,'-')}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-brand text-white shadow" : "text-slate-600 hover:bg-accent hover:text-slate-900"}`}>
            <it.icon size={18} /> {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="px-3 mb-2">
          <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
          <p className="text-[11px] text-slate-400 capitalize">{user?.role}</p>
        </div>
        <button onClick={doLogout} data-testid="btn-logout"
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-accent hover:text-slate-900 transition-colors">
          <LogOut size={18} /> Esci
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-border flex-col fixed inset-y-0 left-0 z-40">
        <SideContent />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-border h-14 flex items-center justify-between px-4">
        <img src={logoFull} alt="TENDItaly" className="h-8 object-contain" />
        <button onClick={() => setOpen(!open)} data-testid="btn-mobile-menu" className="text-slate-700 p-2">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-white flex flex-col h-full fade-in"><SideContent /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 min-w-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto fade-in">{children}</div>
      </main>
    </div>
  );
}
